import { useEffect, useState } from "react";
import { css, html } from "react-strict-dom";
import {
  Dialog,
  Button,
  Input,
  Text,
  Heading,
  Toggle,
  ToggleGroup,
} from "@duro-app/ui";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { borders } from "@duro-app/tokens/tokens/borders.css";
import { spacing } from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { STARTER_TEMPLATES } from "@website-builder/editor";

import {
  listDrafts,
  createDraft,
  deleteDraft,
  type LocalDraft,
} from "../local/draftStore.js";
import { api, type SiteSummary } from "../api/client.js";
import { relativeTime } from "../util/relativeTime.js";

const styles = css.create({
  list: {
    listStyle: "none",
    padding: 0,
    marginBlock: spacing.sm,
    marginInline: 0,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: spacing.sm,
    paddingBlock: spacing.sm,
    paddingInline: spacing.xs,
    borderBottomWidth: borders.hairline,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
  },
  rowActive: {
    backgroundColor: colors.bgCardHover,
  },
  grow: {
    flex: 1,
    minWidth: 0,
  },
  section: {
    marginTop: spacing.lg,
  },
  templates: {
    marginBlock: spacing.sm,
    marginInline: 0,
  },
  preview: {
    display: "block",
    fontSize: typography.fontSizeXl,
  },
  createRow: {
    display: "flex",
    gap: spacing.sm,
    alignItems: "center",
  },
});

export interface DraftsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly activeId: string;
  readonly signedIn: boolean;
  readonly onOpenDraft: (id: string) => void;
  readonly onOpenCloudSite: (id: string) => void;
}

/**
 * Drafts manager — a dialog over the editor (the builder has no separate
 * dashboard route). Lists this browser's account-free drafts and, when
 * signed in, the user's published cloud sites. The "New draft" form picks a
 * starter template and opens straight into the editor — no login required.
 */
export function DraftsDialog({
  open,
  onClose,
  activeId,
  signedIn,
  onOpenDraft,
  onOpenCloudSite,
}: DraftsDialogProps) {
  const [drafts, setDrafts] = useState<LocalDraft[]>([]);
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState(STARTER_TEMPLATES[0]!.id);
  const [cloud, setCloud] = useState<ReadonlyArray<SiteSummary> | null>(null);

  // Reload the local list whenever the dialog opens.
  useEffect(() => {
    if (open) setDrafts(listDrafts());
  }, [open]);

  // Fetch published cloud sites (best-effort) when signed in.
  useEffect(() => {
    if (!open || !signedIn) return;
    let cancelled = false;
    api
      .listSites()
      .then((sites) => {
        if (!cancelled) setCloud(sites);
      })
      .catch(() => {
        if (!cancelled) setCloud([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, signedIn]);

  function handleCreate() {
    const draft = createDraft({
      name: name.trim() || "Untitled site",
      templateId,
    });
    setName("");
    onOpenDraft(draft.id);
  }

  async function handleDelete(id: string) {
    await deleteDraft(id);
    setDrafts(listDrafts());
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal size="md">
        <Dialog.Header>
          <Dialog.Title>Your sites</Dialog.Title>
          <Dialog.Close aria-label="Close" />
        </Dialog.Header>
        <Dialog.Body>
          {/* ── Local drafts ── */}
          <Heading level={2} variant="headingSm">
            Drafts on this device
          </Heading>
          {drafts.length === 0 ? (
            <Text variant="bodySm" color="muted">
              No drafts yet — create one below.
            </Text>
          ) : (
            <html.ul style={styles.list}>
              {drafts.map((d) => (
                <html.li
                  key={d.id}
                  style={[styles.row, d.id === activeId && styles.rowActive]}
                >
                  <html.div style={styles.grow}>
                    <Button variant="link" onClick={() => onOpenDraft(d.id)}>
                      <Text variant="bodyMd" weight="semibold">
                        {d.name}
                      </Text>{" "}
                      <Text variant="caption" color="muted">
                        edited {relativeTime(Math.floor(d.updatedAt / 1000))}
                        {d.cloudSiteId ? " · online" : ""}
                        {d.id === activeId ? " · editing" : ""}
                      </Text>
                    </Button>
                  </html.div>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => void handleDelete(d.id)}
                    aria-label={`Delete ${d.name}`}
                  >
                    Delete
                  </Button>
                </html.li>
              ))}
            </html.ul>
          )}

          {/* ── New draft ── */}
          <html.div style={styles.section}>
            <Heading level={2} variant="headingSm">
              New draft
            </Heading>
            <html.div style={styles.templates}>
              <ToggleGroup
                wrap
                value={[templateId]}
                onValueChange={(v) => {
                  if (v[0]) setTemplateId(v[0]);
                }}
                aria-label="Starter template"
              >
                {STARTER_TEMPLATES.map((tpl) => (
                  <Toggle key={tpl.id} value={tpl.id}>
                    <html.span style={styles.preview}>{tpl.preview}</html.span>
                    <Text variant="caption" weight="semibold">
                      {tpl.label}
                    </Text>
                  </Toggle>
                ))}
              </ToggleGroup>
            </html.div>
            <html.div style={styles.createRow}>
              <html.div style={styles.grow}>
                <Input
                  name="name"
                  aria-label="Site name"
                  placeholder="Site name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </html.div>
              <Button variant="primary" onClick={handleCreate}>
                Create
              </Button>
            </html.div>
          </html.div>

          {/* ── Published sites (cloud) ── */}
          {signedIn && cloud && cloud.length > 0 && (
            <html.div style={styles.section}>
              <Heading level={2} variant="headingSm">
                Published online
              </Heading>
              <html.ul style={styles.list}>
                {cloud.map((s) => (
                  <html.li key={s.id} style={styles.row}>
                    <Button
                      variant="link"
                      onClick={() => onOpenCloudSite(s.id)}
                    >
                      <Text variant="bodyMd" weight="semibold">
                        {s.name}
                      </Text>{" "}
                      <Text variant="caption" color="muted">
                        {s.slug} · {s.role}
                      </Text>
                    </Button>
                  </html.li>
                ))}
              </html.ul>
            </html.div>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Dialog.Footer>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
